import * as Popover from "@radix-ui/react-popover";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useTranslation } from "@/packages/utils";

function WorkspaceAvatar({
  name,
  color,
}: {
  name: string;
  color?: string | null;
}) {
  const initials = name.slice(0, 2).toUpperCase();
  return (
    <div
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-bold text-white"
      style={{ backgroundColor: color ?? "var(--color-accent)" }}
    >
      {initials}
    </div>
  );
}

export function WorkspaceSwitcher({
  currentSlug,
  onClose,
}: {
  currentSlug: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("sidebar");
  const workspaces = useQuery(api.workspaces.getMyWorkspaces, {});
  const navigate = useNavigate();

  return (
    <Popover.Content
      align="start"
      sideOffset={4}
      className="z-50 w-[220px] rounded-lg border border-border/60 bg-surface p-1 shadow-lg animate-in fade-in-0 zoom-in-95"
    >
      <div className="px-2 py-1 text-[11px] font-medium uppercase text-muted tracking-wider">
        {t("workspaceSwitcher.title")}
      </div>
      {(workspaces ?? []).map((ws) => (
        <button
          key={ws._id}
          type="button"
          onClick={() => {
            navigate({
              to: "/$workspaceSlug/dashboard",
              params: { workspaceSlug: ws.slug },
            });
            onClose();
          }}
          className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 transition-colors duration-150 ${
            ws.slug === currentSlug
              ? "ring-1 ring-accent/40 bg-accent/5"
              : "hover:bg-card-hover/50"
          }`}
        >
          <WorkspaceAvatar
            name={ws.name ?? ""}
            color={ws.accentColor ?? undefined}
          />
          <div className="flex-1 text-left min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="truncate text-[13px] font-medium text-primary">
                {ws.name}
              </span>
              {ws.isDemo && (
                <span className="shrink-0 text-[10px] font-medium text-muted bg-muted/20 rounded px-1 py-0.5">
                  {t("workspaceSwitcher.demoLabel")}
                </span>
              )}
            </div>
            <div className="text-[11px] text-muted capitalize">{ws.role}</div>
          </div>
        </button>
      ))}
      <div className="border-t border-border/60 mt-1 pt-1">
        <button
          type="button"
          onClick={() => {
            navigate({ to: "/onboarding", search: { new: true } });
            onClose();
          }}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-card-hover/50 text-[13px] text-muted transition-colors duration-150"
        >
          <Plus size={14} /> {t("workspaceSwitcher.createWorkspace")}
        </button>
      </div>
    </Popover.Content>
  );
}
